import { v4 as uuid } from 'uuid';

import { pick } from '../../utils/objects';
import { ActivityDuration, DueDateType, ToBeDone } from '../types';
import { PollsAPI } from './api-types';
import { SAFE_EMPTY_STRING } from './constants';
import { Poll } from './types';

export function pollFactory({
  currentUserId,
  courseId,
  classId,
  pollId,
  apiPoll,
  publishPreferences,
  apiQuestion,
  pollSubmissions = 0,
}: {
  currentUserId: MongoId;
  courseId: MongoId;
  classId: MongoId;
  pollId: MongoId;
  apiPoll: PollsAPI.APIPollDetails;
  publishPreferences: PollsAPI.PublishPollDefaults | undefined;
  apiQuestion: PollsAPI.APIQuestion | undefined;
  pollSubmissions?: number;
}): Poll {
  const {
    isAddedToPPT,
    slideType,
    title,
    toBeDone,
    dueDateType,
    dueDateTime,
    questionSet,
    isAnon,
    allowLate,
    deadlineFirst,
    createdOn,
    createdBy,
    editedOn,
    published,
    publishedOn,
    trueNum,
  } = apiPoll;

  return {
    id: pollId,
    courseId,
    classId,
    isAddedToPPT: isAddedToPPT === 1,
    slideType,
    title: title === SAFE_EMPTY_STRING ? '' : title,
    toBeDone,
    dueDateTime,
    isQuestionSet: Boolean(questionSet),
    questions: Boolean(questionSet) && apiQuestion ? [apiQuestion._id] : [],
    questionsById:
      Boolean(questionSet) && apiQuestion
        ? {
            [apiQuestion._id]: {
              id: apiQuestion._id,
              description:
                apiQuestion.details.description.text === SAFE_EMPTY_STRING
                  ? ''
                  : apiQuestion.details.description.text,
              attachments: apiQuestion.details.description.attachments,
              options: apiQuestion.details.options.map((option) => ({
                ...option,
                text: option.text === SAFE_EMPTY_STRING ? '' : option.text,
                optionId: uuid(),
                totalSelections:
                  apiQuestion.stats.optionSelection.find((selection) => selection.num === option.num)
                    ?.numSelected ?? 0,
              })),
              totalAttempts: apiQuestion.stats.numAttempted,
            },
          }
        : {},
    preferences: publishPreferences
      ? {
          ...pick(publishPreferences, 'dueDateType', 'duration'),
          toBeDone: publishPreferences.activityType,
          isAnonymous: Boolean(publishPreferences.isAnon),
          allowLate: Boolean(publishPreferences.allowLate),
          deadlineFirst: Boolean(publishPreferences.deadlineFirst),
        }
      : {
          toBeDone,
          dueDateType,
          duration:
            toBeDone === ToBeDone.PRE_CLASS
              ? ActivityDuration.NOT_APPLICABLE
              : dueDateType === DueDateType.MANUAL
                ? ActivityDuration.MANUALLY_STOPPED
                : ActivityDuration.AFTER_5_MINUTES,
          isAnonymous: Boolean(isAnon),
          allowLate: Boolean(allowLate),
          deadlineFirst: Boolean(deadlineFirst),
        },
    createdOn,
    createdBy,
    editedOn: editedOn ?? -1,
    editedBy: editedOn && editedOn > 0 ? createdBy : null,
    /** we need user data to set it correctly */
    firstAccessedOn: currentUserId === createdBy.userId ? createdOn : -1,
    /** we need user data to set it correctly */
    lastAccessedOn: currentUserId === createdBy.userId ? (editedOn ?? createdOn) : -1,
    publishedOn: published > 0 ? publishedOn : -1,
    /** below mentioned properties are not used in ppt addon, they are assigned a value just for satisfying typescript */
    sequenceNum: trueNum ?? 0,
    comments: undefined,
    nodeType: undefined,
    pollSubmissions,
  };
}
